import Icon from '@/components/ui/AppIcon';

interface TransportOption {
  icon: string;
  title: string;
  description: string;
  details: string[];
  color: string;
}

interface TransportationSectionProps {
  className?: string;
}

const TransportationSection = ({ className = '' }: TransportationSectionProps) => {
  const transportOptions: TransportOption[] = [
    {
      icon: 'TruckIcon',
      title: 'Airport Connectivity',
      description: 'Kempegowda International Airport is easily accessible via multiple routes.',
      details: [
        'Distance: 45 km from Proximus',
        'Travel Time: 60-75 minutes',
        'Airport shuttle service available',
        'Taxi and cab services 24/7',
      ],
      color: 'bg-primary',
    },
    {
      icon: 'BuildingLibraryIcon',
      title: 'Metro Stations',
      description: 'Connect to Bangalore Metro for seamless city-wide travel.',
      details: [
        'IIMB/Apollo Hospital: 700 mtrs',
        'Jayadeva Interchange Metro: 3 km',
        'Direct connectivity to major hubs',
        'Affordable and eco-friendly option',
      ],
      color: 'bg-secondary',
    },
    {
      icon: 'BoltIcon',
      title: 'Local Transport',
      description: 'Multiple transportation options for daily commuting and exploration.',
      details: [
        'Auto-rickshaws readily available',
        'Ola and Uber services 24/7',
        'BMTC bus stops within 500m',
        'Bike rental services nearby',
      ],
      color: 'bg-accent',
    },
    {
      icon: 'MapIcon',
      title: 'Major Highways',
      description: 'Strategic location with easy access to key highways and expressways.',
      details: [
        'NICE Road: 5 km access point',
        'Hosur Road: 8 km connection',
        'Outer Ring Road: 6 km away',
        'Quick access to all city zones',
      ],
      color: 'bg-success',
    },
  ];

  return (
    <section className={`py-16 lg:py-24 bg-white ${className}`}>
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl lg:text-5xl font-bold text-text-primary mb-4">
            Transportation & Connectivity
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Seamlessly connected to every corner of Bangalore. From airport transfers to daily commutes, getting around is effortless from Proximus.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {transportOptions.map((option, index) => (
            <div
              key={index}
              className="bg-card rounded-xl shadow-subtle hover:shadow-elevated transition-all duration-300 overflow-hidden border border-border"
            >
              <div className={`${option.color} p-6`}>
                <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mb-4">
                  <Icon name={option.icon as any} size={28} className="text-white" />
                </div>
                <h3 className="font-display text-2xl font-semibold text-white mb-2">
                  {option.title}
                </h3>
                <p className="text-white/90">{option.description}</p>
              </div>
              
              <div className="p-6">
                <ul className="space-y-3">
                  {option.details.map((detail, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <Icon name="CheckCircleIcon" size={20} className="text-success flex-shrink-0 mt-0.5" />
                      <span className="text-text-secondary">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-2xl p-8 border border-primary/20">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div className="flex-1">
              <h3 className="font-display text-2xl font-semibold text-text-primary mb-2">
                Need Transportation Assistance?
              </h3>
              <p className="text-text-secondary">
                Our concierge team can arrange airport pickups, car rentals, and provide detailed route guidance for your daily commute.
              </p>
            </div>
            <a href="mailto:traveldesk@proximusindia.com?subject=Transportation%20Assistance%20Request&body=Dear%20Proximus%20Travel%20Desk,%0A%0AI%20would%20like%20assistance%20with%20transportation.%20Please%20find%20my%20details%20below:%0A%0AName:%20%0AContact%20Number:%20%0ACheck%20in%20Date:%20%0ACheck%20out%20Date:%20%0ARoom%20Number%20(if%20available):%20%0AService%20Required:%20Airport%20Pickup%20/%20Airport%20Drop%20/%20Car%20Rental%20/%20Local%20Transportation%20/%20Other%0APickup%20Location:%20%0ADestination:%20%0APreferred%20Date%20and%20Time:%20%0ANumber%20of%20Passengers:%20%0AAdditional%20Requirements:%20%0A%0AThank%20you." className="px-8 py-3 bg-primary text-white font-semibold rounded-lg shadow-subtle hover:shadow-elevated hover:bg-primary/90 transition-all duration-300 whitespace-nowrap">
              Contact Concierge
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TransportationSection;